import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "../components/Icons";
import { fmtDate } from "../lib/format";
import { downloadSheetFiles } from "../lib/downloadSheets";

/* The tender room, as a contractor meets it.
 *
 * No account, no portal code to be given in advance — a firm arrives on the
 * link, sees what is being tendered and what documents come with it, registers,
 * and is handed its own access code on the spot. */

interface Pack {
  project: { id: string; name: string; type?: string; location?: string; client?: string };
  tender: {
    ref: string; title: string; note?: string; deadline?: string; scope: string; closed: boolean;
    scopeSections?: { title: string; note?: string; items: { ref: string; description: string; qty?: number; unit?: string; note?: string }[] }[];
  };
  documents: { code: string; title: string; discipline: string; rev?: string; fileUrl?: string; previewUrl?: string; fileName?: string; page?: number }[];
  extraDocs?: { name: string; url: string }[];
  studio: { name: string; email: string };
}

export default function TenderPage() {
  const { token } = useParams();
  const [pack, setPack] = useState<Pack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ firm: "", name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ code: string; again?: boolean } | null>(null);
  const [dl, setDl] = useState<string | null>(null);

  /* Sheets from a combined upload split out as their own single-page PDFs. */
  async function grab(sheets: NonNullable<Pack["documents"]>) {
    if (dl) return;
    try {
      await downloadSheetFiles(sheets.filter((s) => s.fileUrl).map((s) => ({ code: s.code, title: s.title, revision: s.rev, fileUrl: s.fileUrl, fileName: s.fileName, page: s.page })),
        (d, t) => setDl(`Preparing — ${d}/${t}`));
    } finally { setDl(null); }
  }

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await fetch(`/api/tender/${token}`);
        if (!r.ok) throw new Error(r.status === 404 ? "This tender link isn't valid — ask whoever sent it for a current one." : "Couldn't load the tender.");
        const d = (await r.json()) as Pack;
        if (live) setPack(d);
      } catch (e: any) { if (live) setError(e.message); }
    })();
    return () => { live = false; };
  }, [token]);

  async function register() {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/tender/${token}/register`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const d = await r.json() as { ok?: boolean; code?: string; again?: boolean; error?: string };
      if (!r.ok || !d.ok) throw new Error(d.error || "Couldn't register you.");
      setDone({ code: d.code!, again: d.again });
    } catch (e: any) { setError(e.message); }
    setBusy(false);
  }

  if (error && !pack) return <Shell><p className="muted">{error}</p></Shell>;
  if (!pack) return <Shell><p className="muted">Opening the tender…</p></Shell>;

  const { project, tender, documents, studio } = pack;
  const byDiscipline = documents.reduce<Record<string, typeof documents>>((acc, d) => {
    (acc[d.discipline || "Drawings"] ||= []).push(d); return acc;
  }, {});
  const ok = form.firm.trim() && form.name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());

  return (
    <Shell>
      <div className="kicker">{studio.name} · invitation to tender</div>
      <h1 className="serif" style={{ fontSize: "clamp(28px,4vw,42px)", marginTop: 8, lineHeight: 1.1 }}>{project.name}</h1>
      <div className="muted" style={{ marginTop: 6 }}>
        {[project.type, project.location].filter(Boolean).join(" · ")}
        {project.client ? ` · for ${project.client}` : ""}
      </div>

      <div className="card pad" style={{ marginTop: 24 }}>
        <div className="row between wrap" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
              <span className="badge accent mono" style={{ fontSize: 10 }}>{tender.ref}</span>
              <h2 className="serif" style={{ fontSize: 22 }}>{tender.title}</h2>
            </div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
              {tender.scope === "whole" ? "The whole project" : "One package of the works"} · {documents.length} document{documents.length === 1 ? "" : "s"}
            </div>
          </div>
          {tender.deadline && (
            <div style={{ textAlign: "right" }}>
              <div className="kicker" style={{ fontSize: 9 }}>Returns by</div>
              <div className="serif" style={{ fontSize: 19, marginTop: 2 }}>{fmtDate(tender.deadline)}</div>
            </div>
          )}
        </div>
        {tender.note && <p style={{ fontSize: 14.5, lineHeight: 1.7, marginTop: 14, whiteSpace: "pre-wrap" }}>{tender.note}</p>}
        {tender.closed && (
          <div className="panel" style={{ padding: "10px 13px", marginTop: 14, borderLeft: "3px solid var(--warn)" }}>
            <b style={{ fontSize: 13 }}>This tender has closed.</b>
            <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>Contact {studio.email} if you believe it should still be open.</div>
          </div>
        )}
      </div>

      {/* register — the only thing asked of them */}
      {!tender.closed && (
        <div className="card pad" style={{ marginTop: 18 }}>
          {done ? (
            <>
              <div className="row" style={{ gap: 9 }}>
                <Icon.check width={18} style={{ color: "var(--ok)" }} />
                <b style={{ fontSize: 15.5 }}>{done.again ? "You're already registered" : "You're registered"}</b>
              </div>
              <p className="muted" style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.65 }}>
                Your access code is below — keep it. We've emailed it to you as well. The documents are listed underneath;
                everything is downloadable now.
              </p>
              <div className="panel mono" style={{ padding: "14px 16px", marginTop: 12, fontSize: 22, letterSpacing: 2, textAlign: "center" }}>{done.code}</div>
            </>
          ) : (
            <>
              <h3 className="serif" style={{ fontSize: 20 }}>Register to tender</h3>
              <p className="muted" style={{ fontSize: 13.5, marginTop: 6, lineHeight: 1.65, maxWidth: 560 }}>
                Tell us who you are and you'll be given your own access code and the full set of documents. Registering
                doesn't commit you to pricing the work.
              </p>
              <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 16 }}>
                <div className="field"><label>Firm</label><input className="input" value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} placeholder="Company name" /></div>
                <div className="field"><label>Your name</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="field"><label>Email</label><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@firm.com" /></div>
                <div className="field"><label>Phone <span className="dim">(optional)</span></label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              {error && <div style={{ color: "var(--danger)", fontSize: 12.5, marginTop: 10 }}>{error}</div>}
              <button className="btn accent" style={{ marginTop: 14 }} disabled={!ok || busy} onClick={register}>
                <Icon.check width={15} />{busy ? "Registering…" : "Register and get the documents"}
              </button>
            </>
          )}
        </div>
      )}

      {/* the scope of works — what is being priced, line by line */}
      {(tender.scopeSections ?? []).length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div className="kicker" style={{ marginBottom: 10 }}>Scope of works — price against these lines</div>
          {(tender.scopeSections ?? []).map((s, si) => (
            <div key={si} className="card" style={{ overflow: "hidden", marginBottom: 12 }}>
              <div style={{ padding: "11px 16px", borderBottom: s.items.length ? "1px solid var(--line)" : "none" }}>
                <b style={{ fontSize: 14 }}>{String(si + 1).padStart(2, "0")} · {s.title}</b>
                {s.note && <div className="dim" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>{s.note}</div>}
              </div>
              {s.items.map((it, i) => (
                <div key={i} className="row between wrap" style={{ padding: "8px 16px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                  <span className="row" style={{ gap: 10, minWidth: 0, flex: 1 }}>
                    <span className="mono dim" style={{ fontSize: 11, minWidth: 44 }}>{it.ref}</span>
                    <span style={{ fontSize: 13 }}>{it.description}{it.note && <span className="dim" style={{ fontSize: 11.5 }}> · {it.note}</span>}</span>
                  </span>
                  {it.qty != null && <span className="mono dim" style={{ fontSize: 11.5, flexShrink: 0 }}>{it.qty} {it.unit ?? ""}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* documents uploaded onto the tender itself — BOQ, preambles, form of tender */}
      {(pack.extraDocs ?? []).length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div className="kicker" style={{ marginBottom: 10 }}>Tender documents</div>
          <div className="card" style={{ overflow: "hidden" }}>
            {pack.extraDocs!.map((d, i) => (
              <div key={i} className="row between wrap" style={{ padding: "10px 16px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                <span style={{ fontSize: 13.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                <a className="chip" style={{ fontSize: 11, flexShrink: 0 }} href={d.url} target="_blank" rel="noreferrer" download={d.name}>
                  <Icon.download width={11} />Download
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* the documents */}
      <div style={{ marginTop: 26 }}>
        <div className="row between wrap" style={{ gap: 10, marginBottom: 10 }}>
          <span className="kicker">The drawings</span>
          {documents.some((d) => d.fileUrl) && (
            <button className="chip" style={{ fontSize: 11 }} disabled={!!dl} onClick={() => void grab(documents)}>
              <Icon.download width={11} />{dl ?? `Download all (${documents.filter((d) => d.fileUrl).length})`}
            </button>
          )}
        </div>
        {documents.length === 0 ? (
          <p className="muted" style={{ fontSize: 13.5 }}>No documents have been attached to this tender yet. {studio.name} will add them shortly.</p>
        ) : Object.entries(byDiscipline).map(([disc, list]) => (
          <div key={disc} className="card" style={{ overflow: "hidden", marginBottom: 12 }}>
            <div className="row between" style={{ padding: "11px 16px", borderBottom: "1px solid var(--line)" }}>
              <span className="kicker">{disc}</span><span className="dim" style={{ fontSize: 12 }}>{list.length}</span>
            </div>
            {list.map((d, i) => (
              <div key={i} className="row between wrap" style={{ padding: "9px 16px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                <span className="row" style={{ gap: 10, minWidth: 0 }}>
                  <span className="mono dim" style={{ fontSize: 11.5, minWidth: 62 }}>{d.code}</span>
                  <span style={{ fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</span>
                  {d.rev && <span className="badge" style={{ fontSize: 8.5 }}>{d.rev}</span>}
                </span>
                {(d.fileUrl || d.previewUrl) && (
                  <span className="row" style={{ gap: 6 }}>
                    <a className="chip" style={{ fontSize: 11 }} href={d.fileUrl || d.previewUrl} target="_blank" rel="noreferrer"><Icon.arrowUpRight width={11} />Open</a>
                    {d.fileUrl && (
                      <button className="chip" style={{ fontSize: 11 }} disabled={!!dl} onClick={() => void grab([d])}>
                        <Icon.download width={11} />Download
                      </button>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <p className="dim" style={{ fontSize: 12, marginTop: 26, lineHeight: 1.6 }}>
        Queries about this tender go to {studio.email}. Documents remain the property of {studio.name} and are issued for
        tendering purposes only.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "48px clamp(18px,4vw,40px) 90px" }}>{children}</div>
    </div>
  );
}
